import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ExternalLink,
  BookOpen,
  Video,
  Headphones,
  Github,
  Twitter,
  ArrowRight,
} from "lucide-react";
import ModuleNavigation from "../components/ModuleNavigation";

const resourceCategories = [
  {
    id: "scientific",
    title: "Scientific Resources",
    description: "Authoritative sources for climate science research and data",
    resources: [
      {
        title: "IPCC Reports",
        url: "https://www.ipcc.ch/reports/",
        organization: "Intergovernmental Panel on Climate Change",
        description:
          "Comprehensive scientific assessments of climate change, its impacts, and potential response strategies.",
      },
      {
        title: "NASA Climate",
        url: "https://climate.nasa.gov/",
        organization: "National Aeronautics and Space Administration",
        description:
          "Scientific data visualizations, articles, and resources about Earth's changing climate.",
      },
      {
        title: "NOAA Climate.gov",
        url: "https://www.climate.gov/",
        organization: "National Oceanic and Atmospheric Administration",
        description:
          "Science and information for a climate-smart nation, with maps, data, and teaching resources.",
      },
    ],
  },
  {
    id: "media",
    title: "Media Resources",
    description:
      "Documentaries, podcasts, and interactive media to deepen your understanding",
    resources: [
      {
        title: "Hot Mess",
        url: "https://www.youtube.com/playlist?list=PLZ8TbKC_HFks3FVO8-EGFvG-pZ6wW6Z4P",
        organization: "PBS Digital Studios",
        description:
          "YouTube series exploring the complex and sometimes confusing topic of climate change.",
      },
      {
        title: "How to Save a Planet",
        url: "https://gimletmedia.com/shows/howtosaveaplanet",
        organization: "Gimlet Media",
        description:
          "Podcast that asks the big questions: what do we need to do to solve the climate crisis, and how do we get it done?",
      },
      {
        title: "Climate Visualizations",
        url: "https://www.carbonbrief.org/mapped-how-climate-change-affects-extreme-weather-around-the-world",
        organization: "Carbon Brief",
        description:
          "Interactive maps and visualizations that show the links between climate change and extreme weather events.",
      },
    ],
  },
  {
    id: "action",
    title: "Action-Oriented Resources",
    description: "Tools and platforms to help you take practical steps",
    resources: [
      {
        title: "Project Drawdown",
        url: "https://drawdown.org/",
        organization: "Project Drawdown",
        description:
          "Comprehensive review of climate solutions with information on their relative impact and cost.",
      },
      {
        title: "Carbon Footprint Calculator",
        url: "https://www.footprintcalculator.org/",
        organization: "Global Footprint Network",
        description:
          "Detailed calculator to measure your personal ecological footprint and explore ways to reduce it.",
      },
      {
        title: "Climate Action Tracker",
        url: "https://climateactiontracker.org/",
        organization: "Climate Analytics and NewClimate Institute",
        description:
          "Independent scientific analysis that tracks government climate action and measures it against the Paris Agreement.",
      },
    ],
  },
];

const successStories = [
  {
    title: "Solar Power Price Drop",
    description:
      "Solar electricity costs have fallen by 89% since 2010, making it the cheapest form of electricity in history in many regions.",
    impact:
      "This price drop has accelerated renewable energy adoption globally, with solar installations growing exponentially each year.",
  },
  {
    title: "Ozone Layer Recovery",
    description:
      "The 1987 Montreal Protocol successfully phased out ozone-depleting substances, and the ozone layer is now on track to fully recover by 2060.",
    impact:
      "This international agreement proves that global cooperation on environmental issues can succeed, providing a model for climate action.",
  },
  {
    title: "Electric Vehicle Revolution",
    description:
      "Electric vehicles reached 14% of new car sales globally in 2022, up from less than 2% in 2018, with some countries reaching over 80% for new sales.",
    impact:
      "The rapid adoption of EVs is reducing transportation emissions and driving innovation in battery technology and clean energy storage.",
  },
  {
    title: "Reforestation Successes",
    description:
      "Countries like Costa Rica have doubled their forest cover from 26% to over 52% in just a few decades through dedicated conservation policies.",
    impact:
      "These forests now sequester carbon, protect biodiversity, and provide ecosystem services like clean water and erosion control.",
  },
];

const FurtherLearning = () => {
  const [isCompleted, setIsCompleted] = useState(false);

  const handleMarkComplete = () => {
    setIsCompleted(!isCompleted);
    // In a real app, you would save this to localStorage or a backend
  };

  const handleSocialClick = (platform) => {
    // In a real app, this would open a link to the platform
    alert(`Opening ${platform} page would happen here`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-10">
        <h1 className="text-4xl font-bold text-primary-600 mb-2">
          Further Learning & Success Stories
        </h1>
        <div className="w-24 h-1 bg-edu-orange mx-auto mt-2 mb-6"></div>
      </div>

      <div className="bg-primary-100 p-6 rounded-lg mb-12">
        <p className="text-lg text-gray-800">
          Your climate literacy journey doesn't end here. This curated
          collection of resources will help you continue learning, while the
          success stories remind us that positive change is possible and already
          happening.
        </p>
      </div>

      {/* Resource Collections */}
      <section className="mb-16">
        <h2 className="text-2xl font-semibold text-gray-800 mb-8">
          Curated Resources
        </h2>

        {resourceCategories.map((category) => (
          <div key={category.id} className="mb-12">
            <div className="flex items-center mb-4">
              {category.id === "scientific" && (
                <BookOpen className="w-5 h-5 text-primary-500 mr-2" />
              )}
              {category.id === "media" && (
                <Video className="w-5 h-5 text-primary-500 mr-2" />
              )}
              {category.id === "action" && (
                <Github className="w-5 h-5 text-primary-500 mr-2" />
              )}
              <h3 className="text-xl font-medium text-gray-800">
                {category.title}
              </h3>
            </div>
            <p className="text-gray-600 mb-6">{category.description}</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {category.resources.map((resource, index) => (
                <div
                  key={index}
                  className="bg-white shadow-md rounded-lg overflow-hidden"
                >
                  <div className="p-6">
                    <h4 className="font-medium text-gray-900 mb-1">
                      {resource.title}
                    </h4>
                    <p className="text-sm text-primary-500 mb-3">
                      {resource.organization}
                    </p>
                    <p className="text-sm text-gray-600 mb-4">
                      {resource.description}
                    </p>
                    <a
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-edu-blue hover:text-edu-orange flex items-center text-sm font-medium"
                    >
                      Visit Resource
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* Success Stories */}
      <section className="mb-16">
        <h2 className="text-2xl font-semibold text-gray-800 mb-8">
          Climate Success Stories
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {successStories.map((story, index) => (
            <div
              key={index}
              className="bg-white shadow-md rounded-lg p-6 border-l-4 border-edu-orange"
            >
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                {story.title}
              </h3>
              <p className="text-gray-600 mb-4">{story.description}</p>
              <div className="bg-primary-100 p-3 rounded">
                <p className="text-sm text-gray-700">
                  <strong>Impact:</strong> {story.impact}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Stay Connected */}
      <section className="bg-primary-700 text-white rounded-lg p-8 mb-16">
        <h2 className="text-2xl font-semibold mb-6">Stay Connected</h2>
        <p className="text-lg mb-8">
          "We don't inherit the Earth from our ancestors; we borrow it from our
          children."
        </p>

        <div className="flex flex-wrap gap-4">
          <button
            onClick={() => handleSocialClick("Twitter")}
            className="bg-white text-primary-700 hover:text-primary-500 px-4 py-2 rounded-lg flex items-center"
            aria-label="Follow us on Twitter"
          >
            <Twitter className="w-5 h-5 mr-2" />
            Follow us on Twitter
          </button>
          <button
            onClick={() => handleSocialClick("Podcast")}
            className="bg-white text-primary-700 hover:text-primary-500 px-4 py-2 rounded-lg flex items-center"
            aria-label="Listen to our Podcast"
          >
            <Headphones className="w-5 h-5 mr-2" />
            Listen to our Podcast
          </button>
          <button
            onClick={() => handleSocialClick("Newsletter")}
            className="bg-white text-primary-700 hover:text-primary-500 px-4 py-2 rounded-lg flex items-center"
            aria-label="Subscribe to our Newsletter"
          >
            <BookOpen className="w-5 h-5 mr-2" />
            Subscribe to our Newsletter
          </button>
        </div>
      </section>

      {/* Course Completion */}
      <section className="text-center mb-12">
        <div className="inline-block bg-primary-100 p-8 rounded-lg">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Congratulations!
          </h2>
          <p className="text-lg text-gray-600 mb-6">
            You've completed the Carbon Literacy Course. Remember that every
            action, no matter how small, contributes to the larger goal of
            creating a sustainable future.
          </p>
          <Link
            to="/"
            className="inline-flex items-center bg-primary-500 text-white px-6 py-3 rounded-lg text-lg font-medium hover:bg-primary-700"
          >
            Return to Course Home
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Navigation - Replace with ModuleNavigation */}
      <ModuleNavigation
        prevLink="/action-toolkit"
        prevText="Previous: Action Toolkit"
        nextLink="/"
        nextText="Back to Home"
        onMarkComplete={handleMarkComplete}
        isCompleted={isCompleted}
      />
    </div>
  );
};

export default FurtherLearning;
